import type { QueryClient } from "@tanstack/react-query";

/**
 * Handler invoked when a module is toggled (activation changed).
 * Integrations can register these to clear integration-specific cache (e.g. feed registration state).
 */
export type ModuleToggleHandler = (queryClient: QueryClient) => void;

const handlers = new Map<string, ModuleToggleHandler>();
/** Handlers keyed by module name only; run when any integration's module with that name is toggled. */
const handlersByModule = new Map<string, ModuleToggleHandler[]>();

function key(integrationName: string, moduleName: string): string {
  return `${integrationName.toLowerCase()}:${moduleName.toLowerCase()}`;
}

/**
 * Register a handler to run when a specific integration module is toggled.
 * Use this for integration-specific cache invalidation (e.g. removing stale feed registration data).
 */
export function registerModuleToggleHandler(
  integrationName: string,
  moduleName: string,
  handler: ModuleToggleHandler
): void {
  handlers.set(key(integrationName, moduleName), handler);
}

/**
 * Register a handler to run when any integration's module with the given name is toggled.
 * Use for module-level cache invalidation (e.g. reporting available sources) so you don't need to register per integration.
 */
export function registerModuleToggleHandlerByModule(
  moduleName: string,
  handler: ModuleToggleHandler
): void {
  const k = moduleName.toLowerCase();
  const list = handlersByModule.get(k) ?? [];
  list.push(handler);
  handlersByModule.set(k, list);
}

/**
 * Run any registered handlers for the given integration and module.
 * Called by the generic module configuration hook after a toggle.
 *
 * Always invalidates `[moduleName, "moduleStatus"]` queries so any consumer
 * of `useModuleStatus(moduleName, ...)` sees fresh data without needing its
 * own toggle handler.
 */
export function runModuleToggleHandlers(
  integrationName: string,
  moduleName: string,
  queryClient: QueryClient
): void {
  queryClient.invalidateQueries({ queryKey: [moduleName, "moduleStatus"] });
  const specificHandler = handlers.get(key(integrationName, moduleName));
  specificHandler?.(queryClient);
  const byModule = handlersByModule.get(moduleName.toLowerCase()) ?? [];
  byModule.forEach((h) => h(queryClient));
}
